import { createAsyncThunk, createSlice, Dispatch } from '@reduxjs/toolkit';
import { RootState } from 'app/store/rootReducer';
import { BudgetSpendByMonth, ListBudgetSpendByMonthsQueryVariables, ModelSortDirection } from 'API';
// SEE-1232 row h: read from CDK. The superseded Amplify document stays in graphql/queries
// (SEE-1238 removes it).
import { cardService } from 'app/services/card.service';

export interface SpendWidgetState {
  spendWidget: BudgetSpendByMonth[] | null | undefined;
  averageMonthlySpend: number;
}

const initialState: SpendWidgetState = {
  spendWidget: [],
  averageMonthlySpend: 0,
};

const getListBudgetSpendByMonths = async (
  { paidolId, limit, sortDirection }: Omit<ListBudgetSpendByMonthsQueryVariables, 'yearAndMonth'>,
  // Kept so both thunks keep their signatures; the CDK read needs no dispatch.
  _thunkApi?: { dispatch: Dispatch }
) => {
  // Transport flip only — same arguments, one page, same items. Neither caller ever
  // looped on nextToken (a page is 100 months by default), so this doesn't either.
  // `paidolId` is optional on the Amplify variables type but both callers always pass the selected
  // company's id; CDK declares it `ID!`.
  return cardService.listBudgetSpendByMonths<BudgetSpendByMonth>({
    paidolId: paidolId as string,
    limit,
    sortDirection,
  });
};

export const getSpendWidgetByMonths = createAsyncThunk(
  'cards/overview/getSpendWidgetByMonths',
  getListBudgetSpendByMonths
);

export const getAverageMonthlySpend = createAsyncThunk(
  'cards/overview/getAverageMonthlySpend',
  (paidolId: string, { dispatch }) =>
    getListBudgetSpendByMonths({ paidolId, limit: 6, sortDirection: ModelSortDirection.DESC }, { dispatch })
);

const spendSlice = createSlice({
  name: 'cards/spendWidget',
  initialState,
  reducers: {
    resetSpendWidgetSlice: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(getSpendWidgetByMonths.fulfilled, (state, action) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      state.spendWidget = action.payload ?? initialState.spendWidget;
    });
    builder.addCase(getAverageMonthlySpend.fulfilled, (state, action) => {
      if (!action.payload) return;
      state.averageMonthlySpend = action.payload.length
        ? action.payload?.reduce((acc, item) => acc + (item?.amount || 0), 0) / action.payload?.length
        : 0;
    });
  },
});

export const { resetSpendWidgetSlice } = spendSlice.actions;

export const selectSpendWidgetSlice = (state: RootState): SpendWidgetState =>
  state?.cards?.spendWidget ?? initialState;

export const selectAverageMonthlySpend = (state: RootState): number =>
  state?.cards?.spendWidget.averageMonthlySpend ?? 0;

export default spendSlice.reducer;
